import { useEffect, useRef, useState } from "react";
import config from "../config/index.js";
import { formatUsdcCompact, liveLoanInterest, parseUsdc } from "../utils/format.js";
import { useModalA11y } from "../hooks/useModalA11y.js";

/* -------------------------------------------------------------------------- */
/*  Repay choice modal — Full / Minimum / Custom                              */
/* -------------------------------------------------------------------------- */

/**
 * Lets the borrower choose HOW MUCH to repay:
 *   - Full Settlement   → whole debt, closes the loan
 *   - Minimum (interest)→ just the accrued interest, principal untouched
 *   - Custom            → any amount up to the full debt
 *
 * `state` is `{ loan, principal, accruedInterest, accrual }` (BigInt
 * wei, 6-dp) or null when closed. While the live debt is still loading,
 * principal / accruedInterest are null and the modal shows a loading
 * row.
 *
 * If `state.accrual` is provided (a snapshot of the loan's stored
 * interest + `lastAccruedAt` + `aprBps` at modal-open), the modal
 * runs its own 1-second ticker and recomputes accruedInterest on the
 * fly — so the displayed Full Settlement / Interest figures tick up
 * visibly instead of freezing at the open-time snapshot. Without
 * `accrual`, the modal falls back to the static `accruedInterest`
 * value (legacy callers).
 *
 * Shared by the My Loans list and the single-loan detail page so the
 * "Pay Now" experience is identical on both surfaces.
 */
export default function RepayChoiceModal({ state, busy, onClose, onConfirm }) {
    const [mode, setMode] = useState("full");
    const [custom, setCustom] = useState("");
    const [tick, setTick] = useState(0);
    const loanId = state?.loan?.id ?? null;
    const dialogRef = useRef(null);
    // The custom field is UNCONTROLLED (driven by this ref, not a `value`
    // prop). The parent pages run their own 1-second tickers that
    // re-render this modal continuously; a controlled input would have
    // React rewrite the field on every tick, fighting the caret and (in
    // some browsers) dropping keystrokes. `custom` below is only a mirror
    // for validation / button label — it never drives the DOM node.
    const customRef = useRef(null);
    // Escape / focus trap / return focus — suppressed while a repay tx
    // is in flight so a stray keystroke can't tear down the wallet flow.
    useModalA11y({
        open: Boolean(state),
        onClose: () => !busy && onClose?.(),
        containerRef: dialogRef
    });

    useEffect(() => {
        // Reset to the default choice whenever a different loan opens.
        setMode("full");
        setCustom("");
        if (customRef.current) customRef.current.value = "";
    }, [loanId]);

    // Per-second ticker so the displayed accrued interest advances
    // while the modal is open. Mounted once — cheap, and stops the
    // value from drifting away from what the page header shows.
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    if (!state) return null;

    const loading = state.principal == null || state.accruedInterest == null;
    const principal = loading ? 0n : state.principal;
    // Live-recompute if the parent passed an `accrual` snapshot; tick
    // is referenced so React's lint catches the per-second dep.
    void tick;
    const accruedInterest = loading
        ? 0n
        : state.accrual
            ? liveLoanInterest(state.accrual) // chain-time default
            : state.accruedInterest;
    const full = principal + accruedInterest;
    const minimum = accruedInterest; // interest-only keeps the loan from growing

    let customWei = 0n;
    if (custom) {
        const n = Number(custom);
        if (Number.isFinite(n) && n > 0) {
            try {
                customWei = parseUsdc(custom);
            } catch {
                customWei = 0n;
            }
        }
    }
    const customValid = customWei > 0n && customWei <= full;
    const customExceeds = customWei > full;

    let chosenWei = 0n;
    let isFull = false;
    if (mode === "full") {
        chosenWei = full;
        isFull = true;
    } else if (mode === "minimum") {
        chosenWei = minimum;
    } else if (mode === "custom") {
        chosenWei = customValid ? customWei : 0n;
    }

    const minimumDisabled = minimum === 0n;
    const canConfirm =
        !busy &&
        !loading &&
        chosenWei > 0n &&
        (mode !== "custom" || customValid) &&
        (mode !== "minimum" || !minimumDisabled);

    const options = [
        {
            id: "full",
            title: "Full Settlement",
            sub: "Clears the loan completely.",
            amount: full
        },
        {
            id: "minimum",
            title: "Minimum (Interest Only)",
            sub: minimumDisabled
                ? "No interest has accrued yet."
                : "Pays accrued interest; principal stays borrowed.",
            amount: minimum,
            disabled: minimumDisabled
        },
        {
            id: "custom",
            title: "Custom Amount",
            sub: "Repay any part of the debt.",
            amount: null
        }
    ];

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4"
            role="presentation"
            onClick={() => !busy && onClose?.()}
        >
            <div
                ref={dialogRef}
                className="w-full max-w-md rounded-[16px] bg-[#10281b] ring-1 ring-[#bfb67b] p-5"
                role="dialog"
                aria-modal="true"
                aria-labelledby="repay-choice-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-start justify-between">
                    <div>
                        <h3
                            id="repay-choice-title"
                            className="font-manrope text-[15px] font-bold text-[#e6c985]"
                        >
                            Repay Loan #{String(loanId).padStart(6, "0")}
                        </h3>
                        <p className="mt-0.5 text-[11px] text-[#e6c985]/70">
                            Choose how much to pay back.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="rounded-md p-1 text-[#e6c985]/70 hover:text-[#e6c985] disabled:opacity-50"
                        aria-label="Close"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                            <line x1="5" y1="5" x2="15" y2="15" />
                            <line x1="15" y1="5" x2="5" y2="15" />
                        </svg>
                    </button>
                </div>

                {/* Debt breakdown */}
                <div className="mt-3 rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-2.5 text-[11px] text-white">
                    {loading ? (
                        <div className="text-center text-[#e6c985]/70">Loading live balance…</div>
                    ) : (
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-[#e6c985]/75">
                                Principal ${formatUsdcCompact(principal)} + Interest ${formatUsdcCompact(accruedInterest)}
                            </span>
                            <span className="font-manrope text-[13px] font-bold text-[#FBC206]">
                                ${formatUsdcCompact(full)} USDC
                            </span>
                        </div>
                    )}
                </div>

                {/* Options */}
                <div className="mt-3 space-y-2">
                    {options.map((opt) => {
                        const active = mode === opt.id;
                        return (
                            <button
                                key={opt.id}
                                type="button"
                                disabled={opt.disabled || busy || loading}
                                onClick={() => setMode(opt.id)}
                                className={`flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-left ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                                    active
                                        ? "bg-[#FBC206] text-[#10281b] ring-[#FBC206]"
                                        : "bg-white/10 text-white ring-[#bfb67b] hover:bg-white/20"
                                }`}
                            >
                                <div className="min-w-0">
                                    <div className="font-manrope text-[12.5px] font-bold">{opt.title}</div>
                                    <div className={`mt-0.5 text-[10px] ${active ? "text-[#10281b]/75" : "text-[#e6c985]/70"}`}>
                                        {opt.sub}
                                    </div>
                                </div>
                                {opt.amount != null ? (
                                    <span className="ml-3 shrink-0 font-manrope text-[12.5px] font-bold">
                                        ${formatUsdcCompact(opt.amount)}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>

                {/* Custom amount input */}
                {mode === "custom" ? (
                    <div className="mt-2.5">
                        <div className="flex items-center gap-2 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5">
                            <span className="font-manrope text-[16px] font-bold text-[#e6c985]/45">$</span>
                            <input
                                ref={customRef}
                                type="text"
                                inputMode="decimal"
                                defaultValue={custom}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    // Accept only an empty field or a partial
                                    // decimal ("", "1", "1.", "1.50"). On a
                                    // rejected keystroke we revert the DOM by
                                    // hand (the field is uncontrolled, so React
                                    // won't do it for us) and otherwise just
                                    // mirror the value into state for the
                                    // validation + button-label logic.
                                    if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                                        setCustom(raw);
                                    } else {
                                        e.target.value = custom;
                                    }
                                }}
                                placeholder="0.00"
                                disabled={busy || loading}
                                className="flex-1 min-w-0 bg-transparent font-manrope text-[16px] font-bold text-white placeholder:text-white/30 focus:outline-none"
                            />
                            <button
                                type="button"
                                disabled={busy || loading}
                                onClick={() => {
                                    // Uncontrolled field: push the value into
                                    // the DOM node ourselves, then mirror it to
                                    // state so the label / validation update.
                                    const next = formatUsdcInput(full);
                                    if (customRef.current) customRef.current.value = next;
                                    setCustom(next);
                                }}
                                className="rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-white/20 disabled:opacity-50"
                            >
                                Max
                            </button>
                        </div>
                        {customExceeds ? (
                            <p className="mt-1 text-[10px] font-medium text-[#ff8d6b]">
                                Amount exceeds the ${formatUsdcCompact(full)} total debt.
                            </p>
                        ) : null}
                    </div>
                ) : null}

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="rounded-full px-4 py-1.5 text-[12px] font-semibold text-[#e6c985] ring-1 ring-[#bfb67b] hover:bg-white/10 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!canConfirm}
                        onClick={() => onConfirm(state.loan, chosenWei, isFull)}
                        className="rounded-full bg-[#FBC206] px-5 py-1.5 text-[12px] font-bold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0"
                    >
                        {busy ? "Loading…" : `Pay $${formatUsdcCompact(chosenWei)}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Renders a 6-dp USDC wei BigInt as a plain decimal string suitable for
 * an <input type="number"> value (no thousands separators / units).
 */
function formatUsdcInput(wei) {
    const decimals = config.protocol.usdcDecimals;
    const base = 10n ** BigInt(decimals);
    const whole = wei / base;
    const frac = wei % base;
    if (frac === 0n) return whole.toString();
    const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
    return `${whole}.${fracStr}`;
}
